import { PropTypes } from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { getUserAlias } from 'models/ProfileInfo/selectors'
import { STRING, EVENT_TYPE } from 'resources'
import { sendAnalyticsEvent } from 'services/analytics'
import { deleteReklaimProtectEmailAddress } from 'services/reklaimProtect'
import { formatEmailAddress } from 'utils/email'

import ReklaimModal, { ModalHeader, ModalBody, ModalTitle, ModalButton } from 'components/ReklaimModal'

import { Description, PurpleText, ErrorMessage } from './styles'

export const RemoveEmailAddressModal = ({ isModalOpen, closeModal, emailAddress = '', setEmailAddressData }) => {
  const userAlias = useSelector(getUserAlias)
  const [errorMessage, setErrorMessage] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEmailAddressRemoveFailed, setIsEmailAddressRemoveFailed] = useState(false)

  useEffect(() => {
    if (isModalOpen) {
      sendAnalyticsEvent(EVENT_TYPE.accountEmailAddressRemoveModalView, { emailAddress })
    }
  }, [isModalOpen, emailAddress])

  const handleClose = () => {
    setIsEmailAddressRemoveFailed(false)
    setErrorMessage(null)
    closeModal()
  }

  const handleRemoveEmailAddress = async () => {
    if (!emailAddress) {
      return
    }
    setIsSubmitting(true)
    sendAnalyticsEvent(EVENT_TYPE.accountEmailAddressRemoveModalConfirmClick, { emailAddress })
    setErrorMessage(null)

    try {
      const result = await deleteReklaimProtectEmailAddress(userAlias, { emailAddress })
      if (result?.error) {
        setIsEmailAddressRemoveFailed(true)
        setErrorMessage(STRING.account.removeEmailAddressModal.defaultErrorMessage)
      } else {
        setEmailAddressData((prevEmailAddresses) => {
          const updatedEmailAddresses = prevEmailAddresses.filter((email) => email.emailAddress !== emailAddress)
          return updatedEmailAddresses
        })
        handleClose()
      }
    } catch (error) {
      setIsEmailAddressRemoveFailed(true)
      setErrorMessage(STRING.account.removeEmailAddressModal.defaultErrorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ReklaimModal isOpen={isModalOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose} disabled={isSubmitting} />
      <ModalBody>
        <ModalTitle>{STRING.account.removeEmailAddressModal.removeTitle}</ModalTitle>
        <Description>
          <span>{STRING.account.removeEmailAddressModal.removeMessage}</span>
          <PurpleText>{formatEmailAddress(emailAddress)}</PurpleText>
          <span>{STRING.account.removeEmailAddressModal.questionMark}</span>
        </Description>
        <ModalButton onClick={handleClose} disabled={isSubmitting}>
          {STRING.account.removeEmailAddressModal.cancelButton}
        </ModalButton>
        <ModalButton
          onClick={handleRemoveEmailAddress}
          variant="transparentRed"
          disabled={isEmailAddressRemoveFailed}
          isLoading={isSubmitting}
        >
          {STRING.account.removeEmailAddressModal.removeButton}
        </ModalButton>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </ModalBody>
    </ReklaimModal>
  )
}

RemoveEmailAddressModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  emailAddress: PropTypes.string,
  setEmailAddressData: PropTypes.func.isRequired,
}

export default RemoveEmailAddressModal
